import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import type { ZodTypeAny, z } from 'zod';

import {
  type AppRoute,
  type AppRouteMutation,
  type AppRouter,
  isAppRoute,
} from './helpers';
import { insertParamsIntoPath, type ParamsFromUrl } from './paths';
import { convertQueryParamsToUrlString } from './query';
import type {
  AreAllPropertiesOptional,
  Without,
  ZodInferOrType,
} from './type.utils';

type RecursiveProxyObj<T extends AppRouter> = {
  [TKey in keyof T]: T[TKey] extends AppRoute
    ? AppRouteFunction<T[TKey]>
    : T[TKey] extends AppRouter
      ? RecursiveProxyObj<T[TKey]>
      : never;
};

type AppRouteMutationType<T> = T extends ZodTypeAny ? z.input<T> : T;

/**
 * Extract the path params from the path in the contract
 */
export type PathParamsFromUrl<T extends AppRoute> =
  ParamsFromUrl<T['path']> extends infer U ? U : never;

type AppRouteBodyOrFormData<T extends AppRouteMutation> = AppRouteMutationType<
  T['body']
>;

interface DataReturnArgs<TRoute extends AppRoute> {
  body: TRoute extends AppRouteMutation
    ? AppRouteBodyOrFormData<TRoute>
    : never;
  params: PathParamsFromUrl<TRoute>;
  query: AreAllPropertiesOptional<
    AppRouteMutationType<TRoute['query']>
  > extends false
    ? AppRouteMutationType<TRoute['query']>
    : never;
}

export type ApiRouteResponse<T> = AxiosResponse<ZodInferOrType<T>>;

/**
 * Returned from a mutation or query call
 */
export type AppRouteFunction<TRoute extends AppRoute> =
  AreAllPropertiesOptional<Without<DataReturnArgs<TRoute>, never>> extends true
    ? (
        args?: Without<DataReturnArgs<TRoute>, never>,
      ) => Promise<ApiRouteResponse<TRoute['response']>>
    : (
        args: Without<DataReturnArgs<TRoute>, never>,
      ) => Promise<ApiRouteResponse<TRoute['response']>>;

export interface ClientArgs {
  api: () => AxiosInstance;
  base?: {
    baseUrl: string;
    baseHeaders: Record<string, string>;
  };
}

export const fetchApi = async (
  path: string,
  clientArgs: ClientArgs,
  route: AppRoute,
  body: unknown,
) => {
  const apiFetcher = clientArgs.api() || axios.create();

  if (clientArgs.base) {
    apiFetcher.defaults.baseURL = clientArgs.base.baseUrl;
    apiFetcher.defaults.headers.common = clientArgs.base.baseHeaders || {};
  }

  return apiFetcher({
    url: path,
    method: route.method,
    headers: {
      ...apiFetcher.defaults.headers.common,
      'Content-Type': 'application/json',
    },
    data: body !== null && body !== undefined ? body : undefined,
  });
};

export const getCompleteUrl = (
  query: unknown,
  params: unknown,
  route: AppRoute,
) => {
  const path = insertParamsIntoPath({
    path: route.path,
    params: params as any,
  });
  const queryComponent = convertQueryParamsToUrlString(query);
  return `${path}${queryComponent}`;
};

export const getRouteQuery = <TAppRoute extends AppRoute>(
  route: TAppRoute,
  clientArgs: ClientArgs,
) => {
  return async (inputArgs?: DataReturnArgs<any>) => {
    const completeUrl = getCompleteUrl(
      inputArgs?.query,
      inputArgs?.params,
      route,
    );
    return fetchApi(completeUrl, clientArgs, route, inputArgs?.body);
  };
};

const createNewProxy = (router: AppRouter, args: ClientArgs) => {
  return new Proxy(
    {},
    {
      get: (_, propKey): any => {
        if (typeof propKey === 'string' && propKey in router) {
          const subRouter = router[propKey];

          if (isAppRoute(subRouter)) {
            // If the current router.X is a route, return a function to handle the users args
            return getRouteQuery(subRouter, args);
          }
          return createNewProxy(subRouter, args);
        }

        return createNewProxy(router, args);
      },
    },
  );
};

export type CreateClientReturn<T extends AppRouter> = {
  api: (args: ClientArgs) => RecursiveProxyObj<T>;
};

export const createRESTClient = <T extends AppRouter>(
  router: T,
): CreateClientReturn<T> => {
  return {
    api: (args: ClientArgs) => {
      const proxy = createNewProxy(router, args) as RecursiveProxyObj<T>;
      return proxy;
    },
  };
};
